/**
 * CoursePreviewHover — Wraps a link with a hover-card that lazily
 * fetches /counsellors/degrees/:id/preview and shows entry grades,
 * salary and satisfaction so counsellors can peek without leaving
 * the shortlist.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "@/components/ui/hover-card";
import { GraduationCap, TrendingUp, Smile, Loader2, DollarSign } from "lucide-react";


function StatRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-xs" data-testid={testid}>
      <span className="flex items-center gap-1.5 text-stone-500">
        <Icon className="w-3 h-3" /> {label}
      </span>
      <span className="font-mono text-stone-900">{value}</span>
    </div>
  );
}


export function CoursePreviewHover({ degreeId, children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPreview = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const r = await api.get(`/counsellors/degrees/${degreeId}/preview`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview unavailable");
    } finally {
      setLoading(false);
    }
  };

  const salary = data?.salary?.median_5yr_gbp;
  const sat = data?.satisfaction?.overall_pct;
  const entry = data?.entry?.a_level_typical || data?.entry?.a_level_min;
  const intlCost = data?.cost?.annual_intl_usd;

  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild onMouseEnter={fetchPreview} onFocus={fetchPreview}>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-72 p-3.5 space-y-2"
        data-testid={`course-preview-${degreeId}`}
      >
        {loading && (
          <div className="flex items-center justify-center gap-2 text-xs text-stone-500 py-3">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading preview…
          </div>
        )}
        {error && !loading && (
          <div className="text-xs text-stone-500 italic">{error}</div>
        )}
        {data && !loading && (
          <>
            <div className="pb-2 border-b border-stone-100">
              <div className="text-[10px] text-stone-500 font-underground-sc tracking-wide">
                {data.uni_name || "—"} · {data.country || ""}
              </div>
              <div className="text-sm font-medium leading-tight mt-0.5">
                {data.degree_name || "—"}
              </div>
              <div className="text-[11px] text-stone-500 mt-0.5">
                {data.qualification || ""}
                {data.duration_years ? ` · ${data.duration_years} yrs` : ""}
              </div>
            </div>

            <StatRow
              icon={GraduationCap}
              label="Entry (A-Level)"
              value={entry || "—"}
              testid="preview-entry"
            />
            {data.entry?.required_subjects?.length > 0 && (
              <div className="text-[10px] text-stone-500 -mt-1 pl-4">
                Requires: {data.entry.required_subjects.slice(0, 3).join(", ")}
              </div>
            )}
            <StatRow
              icon={TrendingUp}
              label="Salary (5-yr median)"
              value={salary ? `£${salary.toLocaleString()}` : "—"}
              testid="preview-salary"
            />
            <StatRow
              icon={Smile}
              label="Satisfaction"
              value={sat != null ? `${Math.round(sat)}%` : "—"}
              testid="preview-satisfaction"
            />
            {intlCost && (
              <StatRow
                icon={DollarSign}
                label="Tuition (intl/yr)"
                value={`$${intlCost.toLocaleString()}`}
                testid="preview-cost"
              />
            )}
            <div className="pt-1.5 text-[10px] text-stone-400 italic">
              Click through for full detail →
            </div>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
